import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { CATEGORIES, type DataSource, type Field } from "./catalog";

const TAG_LABELS: Record<NonNullable<DataSource["tag"]>, string> = {
  experimental: "Experimental",
  diagnostic: "Diagnostic",
  derived: "Derived",
};

// Display order is explicit and independent of the catalog's category grouping.
const SOURCE_ORDER = [
  "APP_USAGE",
  "CALL_LOG",
  "PHYSICAL_ACTIVITY",
  "AMBIENT_AUDIO",
  "LIGHT",
  "BLUETOOTH_SCAN",
  "AT_LOCATION",
  "BATTERY_LEVEL",
  "CHARGING_EVENT",
  "SCREEN_EVENT",
  "WIFI_EVENT",
  "SMS_LOG",
  "NOTIFICATION_POSTED",
  "NOTIFICATION_REMOVED",
  "STEP_COUNT",
  "SLEEP",
  "HEALTH_CONNECT",
  "CALENDAR_EVENT",
  "ALIVE",
];

const ALL_SOURCES: DataSource[] = CATEGORIES.flatMap((c) => c.sources);
const SOURCES: DataSource[] = [
  ...SOURCE_ORDER.map((id) => ALL_SOURCES.find((s) => s.source === id)).filter(
    (s): s is DataSource => Boolean(s),
  ),
  ...ALL_SOURCES.filter((s) => !SOURCE_ORDER.includes(s.source)),
];

function FieldList({ fields }: { fields: Field[] }) {
  return (
    <dl className="divide-y divide-border/60">
      {fields.map((f) => (
        <div
          key={f.name}
          className="grid gap-1 py-3 sm:grid-cols-[14rem_1fr] sm:gap-6"
        >
          <dt className="space-y-0.5">
            <code className="font-mono text-sm font-medium break-words text-[#1E293B]">
              {f.name}
            </code>
            <div className="font-mono text-xs text-muted-foreground">
              {f.type}
            </div>
          </dt>
          <dd className="space-y-1 text-sm text-[#334155]">
            <p>{f.desc}</p>
            {f.values && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-[#475569]">Values: </span>
                {f.values}
              </p>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SourceDetails({ source }: { source: DataSource }) {
  const details = [
    ["Collection method", source.method],
    ["Frequency", source.cadence],
    ["Platform", source.platform],
    ["Permission", source.permission],
    ["Privacy", source.privacy],
    ["Notes", source.notes],
  ].filter((detail): detail is [string, string] => Boolean(detail[1]));

  return (
    <>
      <p className="mt-3 text-sm text-[#334155]">{source.measures}</p>
      {details.length > 0 && (
        <dl className="mt-3 space-y-3 rounded-lg border bg-muted/40 px-4 py-3">
          {details.map(([label, value]) => (
            <div
              key={label}
              className="grid gap-1 sm:grid-cols-[11rem_1fr] sm:gap-6"
            >
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
              </dt>
              <dd className="text-sm text-[#334155]">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}

export default function HelpPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [hash]);

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-semibold text-[#1E293B]">Data Reference</h1>

      <div className="mt-8 space-y-12">
        {SOURCES.map((source) => (
          <section
            key={source.source}
            id={source.source}
            className="scroll-mt-4"
          >
            <div className="flex flex-wrap items-center gap-2 border-b pb-2">
              <h2 className="text-lg font-semibold text-[#1E293B]">
                <Link
                  to={{ hash: source.source }}
                  className="group inline-flex items-center gap-1.5 hover:underline"
                >
                  {source.title}
                  <span
                    aria-hidden
                    className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    #
                  </span>
                </Link>
              </h2>
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                {source.source}
              </code>
              {source.tag && (
                <Badge variant="outline" className="text-[10px]">
                  {TAG_LABELS[source.tag]}
                </Badge>
              )}
            </div>
            <SourceDetails source={source} />
            <div className="mt-2">
              <FieldList fields={source.fields} />
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
